import { ButtonDropdown, IconButton, NavItemSeparator, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { QuoteActionType } from '@features/invoices/enums/quoteActionType'

import { ModalId } from '../../../../../../../../enums/modalId'
import { useDuplicateQuote } from '../../hooks/useDuplicateQuote'
import { useEditQuote } from '../../hooks/useEditQuote'
import { useQuoteStatusActions } from '../../hooks/useQuoteStatusActions'
import { useQuoteViewActions } from '../../hooks/useQuoteViewActions'
import { getQuoteActionsMenuItems } from './utils/getQuoteActionsMenuItems'

export const QuoteActionsMenu = (): ReactElement | null => {
  const { t } = useTranslation()

  const { quoteActionsDisplayedAsMenuItems } = useQuoteViewActions()
  const { markQuoteAsSent, markQuoteAsUnsent } = useQuoteStatusActions()
  const { open: openDeleteQuoteModal } = useModal(ModalId.QuoteDeleteConfirmationModal)
  const { duplicateQuote } = useDuplicateQuote()
  const { editQuote } = useEditQuote()

  const quoteActionsMenuItems = useMemo(
    () => getQuoteActionsMenuItems(quoteActionsDisplayedAsMenuItems, t),
    [quoteActionsDisplayedAsMenuItems, t],
  )

  const handleActionSelect = useCallback(
    (_id: string, item: QuoteActionType | NavItemSeparator) => {
      switch (item) {
        case QuoteActionType.Delete:
          openDeleteQuoteModal()
          break
        case QuoteActionType.Edit:
          editQuote()
          break
        case QuoteActionType.MarkAsSent:
          markQuoteAsSent()
          break
        case QuoteActionType.MarkAsUnsent:
          markQuoteAsUnsent()
          break
        case QuoteActionType.Duplicate:
          duplicateQuote()
          break
      }
    },
    [duplicateQuote, editQuote, markQuoteAsSent, markQuoteAsUnsent, openDeleteQuoteModal],
  )

  if (quoteActionsMenuItems.length === 0) {
    return null
  }

  return (
    <ButtonDropdown
      items={quoteActionsMenuItems}
      onSelect={handleActionSelect}
      placement="bottom-end"
      triggerElement={<IconButton icon="ellipsis" variant="outlined" />}
    />
  )
}
